/**
 * The injected data-fetch contract for the shared dashboard render layer.
 *
 * The composition components (`DashboardBody`, `RowDrillPanel`) own ALL
 * render + interaction state (drill toggle, row-drill expansion, table
 * pagination/sort/search, cache invalidation) but NEVER know how to talk to a
 * backend. Each app provides a `DashboardDataSource` adapter that closes over
 * its own API client + request shape:
 *
 * - **Portal** wraps `reportsApi.{tableData,drill,tableRowDrill}` →
 *   `/api/reports/*`.
 * - **Builder preview** wraps `previewApi.{table,drill,tableRowDrill}` →
 *   `/api/builder/preview/*`, closing over the draft payload
 *   (`report_config` + `dashboard_config` + `project_slug`).
 *
 * The seam is drawn at the HTTP boundary: everything above (placement, toggle,
 * bilingual label resolution, 2-level nesting) is shared; only the network
 * call differs. The shared component passes the app only the stable
 * coordinates (`tableId` / `drillId` / `rowValue` / `filters`); app-specific
 * concerns like `project_slug` live entirely inside the adapter, so the shared
 * component cannot regress them.
 */

import type { DrillData, RowDrillData, TableData } from "./types";

/** Sentinel tableId for the legacy singular table (dashboard_explorer). The
 *  app adapter maps this to its "main table" endpoint. */
export const MAIN_TABLE_ID = "__main__";

export interface FetchTableDataArgs {
  tableId: string;
  filters: Record<string, string>;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  limit: number;
  offset: number;
  search?: string;
  /** Aborts the request when the table no longer needs the page (new
   *  filters, unmount). The server stops the query when the request
   *  closes. Not part of the request's identity (`tableDataKey`). */
  signal?: AbortSignal;
}

export interface FetchAllTableRowsArgs {
  tableId: string;
  filters: Record<string, string>;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /** Active free-text search — the export must contain exactly the rows
   *  the user is looking at (all pages of the FILTERED set), not the
   *  unfiltered table. */
  search?: string;
}

export type TableExportFormat = "xlsx" | "csv_latam" | "csv_us";

export interface ExportTableArgs extends FetchAllTableRowsArgs {
  format: TableExportFormat;
  /** Download file name prefix (the server sanitizes it). */
  exportName: string;
}

export interface ExportTableResult {
  /** `"xlsx_as_csv"` when an Excel export needed more than one part and
   *  the server wrote CSV (LATAM) instead. The UI must show it. */
  notice: string | null;
}

export interface FetchDrillArgs {
  drillId: string;
  filters: Record<string, string>;
  /** Aborts the request when the drill closes or the filters change. */
  signal?: AbortSignal;
}

export interface FetchRowDrillArgs {
  tableId: string;
  drillId: string;
  rowValue: string;
  filters: Record<string, string>;
  /** Aborts the request when the drill closes or the filters change. */
  signal?: AbortSignal;
}

export interface DashboardDataSource {
  /** Paginated/sorted/searched page of a named table. */
  fetchTableData(args: FetchTableDataArgs): Promise<TableData>;

  /** The settled page of an identical earlier ``fetchTableData`` call,
   *  synchronously, or ``undefined``. Set by ``withTableDataCache``
   *  (``DashboardBody`` wraps its source); hosts do not implement it. */
  peekTableData?(args: FetchTableDataArgs): TableData | undefined;

  /** ALL rows for export (no pagination; honors the active search).
   *  When absent the export button degrades to the current page only. */
  fetchAllTableRows?(args: FetchAllTableRowsArgs): Promise<Array<Record<string, unknown>>>;

  /** Full export generated on the SERVER in parts (QA-F01): one file, or
   *  a ZIP when it needs 2+ parts, downloaded when ready. When present
   *  the table export uses it instead of `fetchAllTableRows`, which
   *  cannot hold a multi-million-row table in memory. Rejects (and no
   *  file is saved) when the export fails. */
  exportTable?(args: ExportTableArgs): Promise<ExportTableResult>;

  /** KPI-drill payload for a drill_id under the active filters. */
  fetchDrill(args: FetchDrillArgs): Promise<DrillData>;

  /** Row-drill payload for a clicked row value of a table. */
  fetchRowDrill(args: FetchRowDrillArgs): Promise<RowDrillData>;

  /** Narrative readout for the `ai_readout` block (epic v3).
   *
   *  OPTIONAL by design. This prose is the one thing on a scorecard
   *  that no fact table can produce, so it does not come from SQL like
   *  every other block — it comes from the report's AI-analysis
   *  endpoint. A host that cannot serve it (the builder preview) simply
   *  omits the method and the block says so, rather than rendering an
   *  empty card that looks like missing data. */
  fetchAiReadout?(args: FetchAiReadoutArgs): Promise<AiReadout>;

  /** The caller's saved filters as a portfolio (the
   *  `saved_views_portfolio` block, DSA).
   *
   *  OPTIONAL like the readout, for the same reason: the element is the
   *  VIEWER's saved filters, each read as the report's own KPIs — the
   *  portal serves it per user (`/api/reports/{key}/saved-filters/
   *  portfolio`); the builder preview has no caller and omits the
   *  method, and the block says so. */
  fetchSavedViewsPortfolio?(args: FetchSavedViewsPortfolioArgs): Promise<SavedViewsPortfolio>;
}

export interface FetchSavedViewsPortfolioArgs {
  /** The `saved_views_portfolio` chart's item id (its config decides the
   *  KPIs, the states and the leader). */
  itemId: string;
  /** UI language — the KPI captions come back resolved. */
  language: string;
  signal?: AbortSignal;
}

/** A saved filter of the caller, as the saved-filters API lists it. */
export interface SavedViewRef {
  id: string;
  report_key: string;
  name: string;
  /** The dashboard's active-filter dict, restricted to the report's keys. */
  filters: Record<string, string>;
  sku: { count: number; file_name: string; uploaded_at: string } | null;
  /** The value for the report's `sku_list` filter when applying this
   *  view, or null without a list. */
  sku_reference: string | null;
  created_at: string;
  updated_at: string;
}

/** One report KPI read under one saved view. */
export interface SavedViewKpi {
  item_id: string;
  label: unknown;
  value: number | string | null;
  format?: string | null;
  unit?: string | null;
  /** The KPI's caption, already in the UI language. */
  caption?: string | null;
}

export interface SavedViewEntry {
  saved: SavedViewRef;
  kpis: SavedViewKpi[];
  /** A state of the chart's `portfolio.status.states`. */
  status: string;
  /** Who leads this view (the winner KPI's caption or value), or null. */
  winner: string | null;
}

export interface SavedViewsPortfolioSummary {
  views: number;
  with_presence: number;
  lead: number;
  behind: number;
  win_rate: number | null;
  status_counts: Record<string, number>;
  leaders: Array<{ label: string; views: number; share: number; focus: boolean }>;
}

export interface SavedViewsPortfolio {
  report_key: string;
  item_id: string;
  generated_at: string;
  views: SavedViewEntry[];
  summary: SavedViewsPortfolioSummary;
}

export interface FetchAiReadoutArgs {
  activeFilters: Record<string, string>;
  /** UI language, forwarded so the prose comes back translated. */
  language: string;
  signal?: AbortSignal;
}

export interface AiReadout {
  executive_summary: string;
  insights: Array<{ title: string; summary: string }>;
}
